import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import Copy from '@/images/Copy.svg';
import PlayCircleOutlined from '@/images/PlayCircleOutlined.svg';
import theme from '@/theme';
import { Button } from '@de-id/ui-components';
import styled from '@emotion/styled';
import { CircularProgress } from '@mui/material';
import { useState } from 'react';
import { useWebhookComponentContext } from '../../contex';

const TestTabContentContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
    margin-top: 24px;
    flex: 1;
`;

const RunTestButton = styled(Button)`
    width: fit-content;
`;

const TextAreaContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    border-radius: 8px;
    flex: 1;
    overflow: hidden;
`;

const StyledTextArea = styled.textarea<{ value: string }>`
    background-color: #171717;
    color: ${props => (props.value.trim() === '{}' ? theme.colors.white : theme.colors.primary.main)};
    padding: 10px;
    padding-left: 16px;
    padding-right: 40px; /* Space for copy button */
    white-space: pre-wrap;
    word-break: break-all;
    font-family: Menlo, monospace;
    font-size: 13px;
    line-height: 19.5px;
    height: ${props => (props.value.trim() === '{}' ? '40px' : 'auto')};
    min-height: 40px;
    flex-grow: 1;
    flex-shrink: 1;
    min-width: 0;
    overflow-y: auto;
    overflow-x: auto;
    border: none;
    resize: none;
    outline: none;
    scrollbar-width: thin;
    scrollbar-color: #444 #171717;
    border-radius: 8px;
    line-height: 19.5px;

    &::-webkit-scrollbar {
        width: 8px;
        height: 8px;
    }

    &::-webkit-scrollbar-track {
        background: #171717;
    }

    &::-webkit-scrollbar-thumb {
        background: #444;
        border-radius: 4px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
`;

const ContentWrapper = styled.div`
    display: flex;
    flex-direction: row;
    position: relative;
    flex: 1;
`;

const CopyButton = styled(Button)`
    position: absolute;
    top: 8px;
    right: 8px;
    width: 24px;
    height: 24px;
    padding: 4px;
    border: none;
    background-color: transparent !important;

    svg {
        margin-left: 2px;
        color: #eeeeee;
    }
`;

const TestTabFooterContainer = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: auto;
`;

export const TestTabContent = () => {
    const { testResults, runTest, webhookIndex } = useWebhookComponentContext();
    const [loading, setLoading] = useState(false);

    const handleRunTest = async () => {
        setLoading(true);

        await runTest();

        setLoading(false);
    };

    const handleCopy = () => {
        toast.success({ title: 'copied to clipboard' });
        if (testResults) {
            navigator.clipboard.writeText(testResults);
        }
    };

    return (
        <TestTabContentContainer>
            <RunTestButton size="small" color="outlined" onClick={handleRunTest} disabled={loading}>
                {loading ? (
                    <CircularProgress size={16} color="primary" />
                ) : (
                    <MaskedIcon src={PlayCircleOutlined} size="16px" color={theme.colors.primary.main} />
                )}
                Run test
            </RunTestButton>

            {testResults && (
                <TextAreaContainer>
                    <ContentWrapper>
                        <StyledTextArea value={testResults} readOnly />
                    </ContentWrapper>
                    <CopyButton size="small" color="outlined" onClick={handleCopy}>
                        <MaskedIcon src={Copy} size="16px" />
                    </CopyButton>
                </TextAreaContainer>
            )}

            <TestTabFooterContainer>
                <Button size="small" color="secondary" type="submit">
                    {webhookIndex !== null ? 'Save changes' : 'Add Webhook'}
                </Button>
            </TestTabFooterContainer>
        </TestTabContentContainer>
    );
};
